import React from 'react';
import { motion } from 'framer-motion';
import { Store, Users, Soup, Utensils, Beer, Coffee, Building2, Tag } from 'lucide-react';
import { useRegion } from '../../../regions/RegionContext';

const ServicesSection = () => {
  const { region } = useRegion();
  const services = [
    {
      icon: Utensils,
      title: "Independent Restaurants",
      description: "Single-unit excellence"
    },
    {
      icon: Soup,
      title: region === "usa" ? "Ghost Kitchens" : "Cloud Kitchens",
      description: "Delivery-focused dining"
    },
    {
      icon: Coffee,
      title: "Cafés",
      description: "Café culture redefined"
    },
    {
      icon: Store,
      title: "QSRs",
      description: "Quick service excellence"
    },
    {
      icon: Beer,
      title: "Bar & Restaurants",
      description: "Dining & bar experiences"
    },
    {
      icon: Users,
      title: "Pubs & Breweries",
      description: "Beverage-focused venues"
    },
    {
      icon: Building2,
      title: "Restaurant Chains",
      description: "Multi-location operations"
    },
    {
      icon: Tag,
      title: "Franchise Outlets",
      description: "Standardized success"
    }
  ];

  // Stagger animation for cards
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.9
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1] // Custom easing for smooth animation
      }
    }
  };

  return (
    <section className="relative py-16 md:py-24 px-6 bg-white overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Background Elements */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-20 right-10 w-64 h-64 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-yellow-400 blur-3xl"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-14 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
              Who We Serve
            </span>
          </motion.div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.1] mb-4"
            style={{ letterSpacing: '-0.02em' }}
          >
            <span className="text-gray-900">Serving </span>
            <span className="relative inline-block">
              <span className="italic font-serif text-primary relative z-10">Excellence</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 8C50 2, 150 2, 198 8"
                  stroke="#1b7840"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                />
              </svg>
            </span>
            <span className="text-gray-900"> Across</span>{" "}
            <br />
            <span className="text-gray-900">Food Service Landscape</span>
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-sm md:text-base text-gray-600 max-w-3xl mx-auto"
          >
            Tailored solutions for every segment of the restaurant industry
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-5 md:gap-7 lg:gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              className="flex flex-col items-center group"
            >
              {/* Icon Container with Enhanced Blob Shape */}
              <motion.div
                className="relative mb-4"
                whileHover={{ scale: 1.08 }}
                transition={{ duration: 0.3 }}
              >
                {/* Animated Blob Background */}
                <div className="absolute inset-0 transform scale-110">
                  <svg viewBox="0 0 200 200" className="w-24 h-24 md:w-32 md:h-32 lg:w-36 lg:h-36">
                    <motion.path
                      fill="#F6F4EF"
                      d="M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.6,90,-16.3,88.5,-0.9C87,14.6,81.4,29.2,73.1,42.8C64.8,56.4,53.8,69,39.8,76.8C25.8,84.6,8.8,87.6,-7.1,87.1C-23,86.6,-37.9,82.6,-51.4,74.9C-64.9,67.2,-77,55.8,-84.4,42.1C-91.8,28.4,-94.5,12.4,-93.2,-3.2C-91.9,-18.8,-86.6,-34,-77.9,-46.8C-69.2,-59.6,-57.1,-69.9,-43.3,-77.3C-29.5,-84.7,-14.7,-89.2,0.3,-89.7C15.3,-90.2,30.6,-86.6,44.7,-76.4Z"
                      transform="translate(100 100)"
                      className="transition-all duration-500"
                      initial={false}
                      whileHover={{ fill: "rgba(27, 120, 64, 0.1)" }}
                      animate={{
                        d: [
                          "M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.6,90,-16.3,88.5,-0.9C87,14.6,81.4,29.2,73.1,42.8C64.8,56.4,53.8,69,39.8,76.8C25.8,84.6,8.8,87.6,-7.1,87.1C-23,86.6,-37.9,82.6,-51.4,74.9C-64.9,67.2,-77,55.8,-84.4,42.1C-91.8,28.4,-94.5,12.4,-93.2,-3.2C-91.9,-18.8,-86.6,-34,-77.9,-46.8C-69.2,-59.6,-57.1,-69.9,-43.3,-77.3C-29.5,-84.7,-14.7,-89.2,0.3,-89.7C15.3,-90.2,30.6,-86.6,44.7,-76.4Z",
                          "M37.8,-65.5C49.5,-58.3,59.7,-48.1,66.2,-35.8C72.7,-23.5,75.5,-9.1,74.3,4.9C73.1,18.9,68,32.5,59.4,43.2C50.8,53.9,38.7,61.7,25.3,67.1C11.9,72.5,-2.8,75.5,-17.3,73.8C-31.8,72.1,-46.1,65.7,-57.4,55.3C-68.7,44.9,-77,30.5,-79.8,15.1C-82.6,-0.3,-80,-16.7,-72.8,-30.3C-65.6,-43.9,-53.8,-54.7,-40.6,-61.3C-27.4,-67.9,-12.9,-70.3,0.7,-71.4C14.3,-72.5,26.1,-72.7,37.8,-65.5Z",
                          "M44.7,-76.4C58.8,-69.2,71.8,-59.1,79.6,-45.8C87.4,-32.6,90,-16.3,88.5,-0.9C87,14.6,81.4,29.2,73.1,42.8C64.8,56.4,53.8,69,39.8,76.8C25.8,84.6,8.8,87.6,-7.1,87.1C-23,86.6,-37.9,82.6,-51.4,74.9C-64.9,67.2,-77,55.8,-84.4,42.1C-91.8,28.4,-94.5,12.4,-93.2,-3.2C-91.9,-18.8,-86.6,-34,-77.9,-46.8C-69.2,-59.6,-57.1,-69.9,-43.3,-77.3C-29.5,-84.7,-14.7,-89.2,0.3,-89.7C15.3,-90.2,30.6,-86.6,44.7,-76.4Z"
                        ]
                      }}
                      transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </svg>
                </div>

                {/* Glow Effect on Hover */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="w-full h-full rounded-full bg-primary/5 blur-xl" />
                </motion.div>

                {/* Icon */}
                <div className="relative z-10 flex items-center justify-center w-24 h-24 md:w-32 md:h-32 lg:w-36 lg:h-36">
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <service.icon
                      className="w-12 h-12 md:w-16 md:h-16 lg:w-18 lg:h-18 text-primary transition-all duration-300 group-hover:text-primary-dark"
                      strokeWidth={1.5}
                    />
                  </motion.div>
                </div>
              </motion.div>

              {/* Title */}
              <motion.h3
                className="text-gray-900 font-bold text-center text-sm md:text-base mb-1 group-hover:text-primary transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
              >
                {service.title}
              </motion.h3>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-gray-500 text-sm text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                {service.description}
              </motion.p>

              {/* Animated Underline */}
              <motion.div
                className="mt-3 h-0.5 bg-primary rounded-full"
                initial={{ width: 0 }}
                whileHover={{ width: "60%" }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-14 md:mt-16"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a
              href="/services"
              className="inline-block px-8 py-3 bg-primary hover:bg-primary-dark text-white rounded-full font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Explore All Services
            </a>
          </motion.div>
          
          <p className="mt-4 text-gray-500 text-xs md:text-sm">
            Custom solutions designed for your specific needs
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;